'use client'
import { type FormEventHandler, type ReactNode } from 'react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useFetchInsurancePrompt } from '@/components/PriceCalculator/CurrentInsuranceField/FetchInsurancePrompt/useFetchInsurancePrompt'
import { useHandleSubmitPriceCalculatorSection } from '@/components/PriceCalculator/useHandleSubmitPriceCalculatorSection'
import { useTranslateFieldLabel } from '@/components/PriceCalculator/useTranslateFieldLabel'
import { TextLink } from '@/components/TextLink/TextLink'
import { TextWithLink } from '@/components/TextWithLink'
import { FetchInsuranceContainer } from '@/features/insurely/FetchInsurance/FetchInsuranceContainer'
import { EditSsnWarningContainer } from '@/features/priceCalculator/PurchaseForm/InsuranceDataForm/EditSsnWarningContainer'
import { FormGridNew } from '@/features/priceCalculator/PurchaseForm/InsuranceDataForm/FormGridNew/FormGridNew'
import { useConfirmPriceIntent } from '@/features/priceCalculator/PurchaseForm/InsuranceDataForm/useConfirmPriceIntent'
import { deserializeField } from '@/services/PriceCalculator/PriceCalculator.helpers'
import { type FormSection, type JSONData } from '@/services/PriceCalculator/PriceCalculator.types'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { Button } from 'ui/src/components/Button/Button'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { InfoIconOutlined } from 'ui/src/icons/InfoIconOutlined'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { GOTO_NEXT_SECTION, useActiveFormSectionId } from '../../useActiveFormSectionId'
import { usePriceCalculatorForm, useRegistrationAddressState } from '../../usePriceCalculatorForm'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { usePriceIntentValueOrThrow } from '../../usePriceIntent'
import { usePriceCalculatorLoadingState } from '../priceCalculatorAtoms'
import { SectionSubtitle, SectionTitle } from '../SectionHeading'
import { SectionNavigation } from './SectionNavigation/SectionNavigation'
import { SSN_SE_SECTION_ID, SsnSeSectionV2 } from './SsnSeSectionV2'
import {
  gdprLink,
  ssnSectionWrapper,
} from '@/features/priceCalculator/PurchaseForm/InsuranceDataForm/InsuranceDataForm.css'

export function InsuranceDataForm({ className }: { className?: string }) {
  const locale = useRoutingLocale()
  const { t } = useTranslation('purchase-form')

  const form = usePriceCalculatorForm()
  const { activeFormSectionId } = useActiveFormSectionId()
  const { step } = usePriceCalculatorStep()

  const sections = form.sections.map((section, index) => {
    if (step !== 'fillForm' || section.id !== activeFormSectionId) {
      // Skip rendering inactive sections
      return null
    }

    let sectionBody: ReactNode
    const isSsnSection = section.id === SSN_SE_SECTION_ID

    if (isSsnSection) {
      sectionBody = <SsnSeSectionV2 />
    } else {
      const isLast = index === form.sections.length - 1

      sectionBody = (
        <>
          <InsuranceDataSection section={section} />
          {isLast && (
            <TextLink
              href={PageLink.privacyPolicy({ locale })}
              className={clsx(sprinkles({ alignSelf: 'center' }), gdprLink)}
              target="_blank"
              size="xs"
            >
              {t('GDPR_LINK_BEFORE_OFFER')}
            </TextLink>
          )}
        </>
      )
    }
    return (
      <div
        key={section.id}
        className={clsx(yStack({ gap: { _: 'lg', lg: 9 } }), isSsnSection && ssnSectionWrapper, className)}
      >
        {!isSsnSection && <SectionNavigation />}

        <div className={yStack({ gap: 'lg' })}>
          <SectionHeadings section={section} />
          {sectionBody}
        </div>
      </div>
    )
  })

  return (
    <>
      <EditSsnWarningContainer />
      {sections}
      <FetchInsuranceContainer />
    </>
  )
}

function SectionHeadings({ section }: { section: FormSection }) {
  const translateLabel = useTranslateFieldLabel()
  const isLarge = useBreakpoint('lg')

  return (
    <div>
      <div className={xStack({ alignItems: 'center', gap: 'xs' })}>
        <SectionTitle>{translateLabel(section.title)}</SectionTitle>
        {section.tooltip && (
          <ToggleTip
            // We show sticky header on mobile; tooltip should not be overridden by it
            side={isLarge ? 'top' : 'bottom'}
            align="start"
            alignOffset={-12}
            sideOffset={0}
            portal={false}
            collisionPadding={4}
            Trigger={
              <IconButton
                data-no-hover
                Icon={<InfoIconOutlined size="1.25rem" color={tokens.legacyColors.fillOpaqueSecondary} />}
              />
            }
          >
            <TextWithLink
              as="span"
              size="xs"
              color="textNegative"
              href={PageLink.milage()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translateLabel(section.tooltip)}
            </TextWithLink>
          </ToggleTip>
        )}
      </div>
      {section.subtitle != null && <SectionSubtitle>{translateLabel(section.subtitle)}</SectionSubtitle>}
    </div>
  )
}

type InsuranceDataSectionProps = {
  section: FormSection
}

function InsuranceDataSection({ section }: InsuranceDataSectionProps) {
  const confirmPriceIntent = useConfirmPriceIntent()
  const { activeFormSectionId, setActiveFormSectionId } = useActiveFormSectionId()
  const { prompt: promptFetchInsurance } = useFetchInsurancePrompt()
  const priceIntent = usePriceIntentValueOrThrow()
  const [shouldUseRegistrationAddress] = useRegistrationAddressState(priceIntent.id)
  const [isLoading, setIsLoading] = usePriceCalculatorLoadingState()
  const translateLabel = useTranslateFieldLabel()
  const form = usePriceCalculatorForm()

  const submitPriceCalculatorSection = useHandleSubmitPriceCalculatorSection({
    async onSuccess({ priceIntent }) {
      const activeSectionIndex = form.sections.findIndex((section) => section.id === activeFormSectionId)
      const isLastSection = activeSectionIndex === form.sections.length - 1

      if (isLastSection && priceIntent.isReadyToConfirm) {
        const shouldPromptFetchInsurance =
          priceIntent.externalInsurer && priceIntent.insurely && !priceIntent.hasCollectedInsurelyData
        if (shouldPromptFetchInsurance) {
          await promptFetchInsurance()
        }

        confirmPriceIntent(priceIntent)
        return
      }

      setActiveFormSectionId(GOTO_NEXT_SECTION)
      setIsLoading(false)

      // Scroll to top when going to next step to avoid sticky header overlap
      window.scrollTo({ top: 0, behavior: 'instant' })
    },
  })

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    setIsLoading(true)
    const formData = new FormData(event.currentTarget)
    const jsonData = formDataToJson(formData, section.items)
    submitPriceCalculatorSection(jsonData, { applySuggestedData: shouldUseRegistrationAddress })
  }

  return (
    <form onSubmit={handleSubmit} className={yStack({ gap: 'md' })}>
      <FormGridNew items={section.items}>
        <Button type="submit" size="large" loading={isLoading} fullWidth={true}>
          {translateLabel(section.submitLabel)}
        </Button>
      </FormGridNew>
    </form>
  )
}

const formDataToJson = (formData: FormData, sectionItems: FormSection['items']) => {
  const result: JSONData = {}

  for (const item of sectionItems) {
    const deserializedFieldValue = deserializeField(item.field, formData)
    if (deserializedFieldValue !== undefined) {
      result[item.field.name] = deserializedFieldValue
    }
  }
  return result
}
